"use strict";
// @df/api - Helper functions for common API operations
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploadHelper = exports.SearchHelper = exports.PresignedDownloadHelper = exports.PaginationHelper = void 0;
const client_1 = require("./client");
const auth_1 = require("./auth");
// Pagination helper
class PaginationHelper {
    constructor(endpoint, params = {}) {
        this.endpoint = endpoint;
        this.params = params;
    }
    // Get first page
    async getFirstPage() {
        const endpointWithParams = SearchHelper.addSearchToEndpoint(this.endpoint, this.params);
        return client_1.apiClient.get(endpointWithParams);
    }
    // Get next page
    async getNextPage(nextUrl) {
        // Handle absolute URLs by using the shared client for consistency
        if (nextUrl.startsWith('http')) {
            // Extract the path from the absolute URL to use with apiClient
            const url = new URL(nextUrl);
            const path = url.pathname + url.search;
            return client_1.apiClient.get(path);
        }
        // Handle relative URLs with the API client
        return client_1.apiClient.get(nextUrl);
    }
    // Get all pages (use with caution for large datasets)
    async getAllPages() {
        const allItems = [];
        let currentPage = await this.getFirstPage();
        allItems.push(...currentPage.results);
        while (currentPage.next) {
            currentPage = await this.getNextPage(currentPage.next);
            allItems.push(...currentPage.results);
        }
        return allItems;
    }
    // Iterate through pages
    async *iteratePages() {
        let currentPage = await this.getFirstPage();
        yield currentPage;
        while (currentPage.next) {
            currentPage = await this.getNextPage(currentPage.next);
            yield currentPage;
        }
    }
}
exports.PaginationHelper = PaginationHelper;
// Presigned download helper
class PresignedDownloadHelper {
    // Get presigned URL for point cloud file download
    static async getPointCloudDownloadUrl(fileId) {
        const response = await client_1.apiClient.post(`/point-cloud/${fileId}/get_point_cloud_file/`);
        return response.presigned_url;
    }
    // Trigger browser download from presigned URL
    static async downloadFromPresignedUrl(presignedUrl, filename) {
        try {
            const response = await fetch(presignedUrl);
            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            throw new Error(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    // Combined helper: get presigned URL and trigger download
    static async downloadPointCloudFile(fileId, filename) {
        const presignedUrl = await this.getPointCloudDownloadUrl(fileId);
        await this.downloadFromPresignedUrl(presignedUrl, filename);
    }
}
exports.PresignedDownloadHelper = PresignedDownloadHelper;
// Search helper
class SearchHelper {
    // Build search query parameters
    static buildSearchParams(params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                if (Array.isArray(value)) {
                    value.forEach(item => searchParams.append(key, item.toString()));
                }
                else {
                    searchParams.append(key, value.toString());
                }
            }
        });
        return searchParams.toString();
    }
    // Add search parameters to endpoint
    static addSearchToEndpoint(endpoint, params) {
        const searchString = this.buildSearchParams(params);
        if (searchString) {
            const separator = endpoint.includes('?') ? '&' : '?';
            return `${endpoint}${separator}${searchString}`;
        }
        return endpoint;
    }
}
exports.SearchHelper = SearchHelper;
// File upload helper
class FileUploadHelper {
    // Upload file with progress tracking
    static async uploadFile(endpoint, file, onProgress) {
        const formData = new FormData();
        formData.append('file', file);
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            if (onProgress) {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const progress = (event.loaded / event.total) * 100;
                        onProgress(progress);
                    }
                });
            }
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        resolve(response);
                    }
                    catch {
                        resolve(xhr.responseText);
                    }
                }
                else {
                    reject(new Error(`Upload failed: ${xhr.statusText}`));
                }
            });
            xhr.addEventListener('error', () => {
                reject(new Error('Upload failed: Network error'));
            });
            xhr.open('POST', endpoint);
            // Add authentication header
            const authHeader = auth_1.authManager.getAuthHeader();
            if (authHeader) {
                xhr.setRequestHeader('Authorization', authHeader);
            }
            xhr.send(formData);
        });
    }
}
exports.FileUploadHelper = FileUploadHelper;
// All helpers are already exported above with their class declarations
